import { notFoundRouterRoute } from '@townsquare/404-page-view/__router-route';
import { agentStudioRouterRoute } from '@townsquare/agent-studio-view/__router-route';
import { popupAuthRoute } from '@townsquare/auth/popup-login';
import { capabilityProviderResource } from '@townsquare/capability-provider/resource';
import { chatRouterRoute, createAgentRouterRoute, editAgentRouterRoute } from '@townsquare/chat-view/__router-route';
import { dashboardRouterRoute, dashboardDirectoryRouterRoute } from '@townsquare/dashboards-view/__router-route';
import { dashboardExperienceResource } from '@townsquare/dashboards-view/resources';
import {
  createKudosEmbedRouteOld,
  goalEmbedRoute,
  goalsDirectoryEmbedRoute,
  labelEmbedRoute,
  createKudosEmbedRoute,
  projectEmbedRoute,
  projectsDirectoryEmbedRoute,
} from '@townsquare/embeds';
import { emptyOrNonProvisionedStatusUpdateStateRoute } from '@townsquare/empty-or-not-provisioned-state/route';
import { goalViewRoute } from '@townsquare/goal-view/__router-route';
import {
  goalDirectoryRouterRoute,
  goalNoWorkspaceDirectoryRouterRoute,
} from '@townsquare/goals-directory-view/__router-route';
import { sidebarContentResource } from '@townsquare/griffin-navigation/resources';
import { homebarSideNavigationResource } from '@townsquare/home-layout/resources';
import {
  goalUpdateViewRouterRoute,
  projectUpdateViewRouterRoute,
  recentsViewRouterRoute,
  savedViewsUpdatesRouterRoute,
  tagFeedViewRouterRoute,
  teamUpdatesViewRouterRoute,
} from '@townsquare/home-view/__router-route';
import { appsRoute, homeTabRoute } from '@townsquare/hometab-view';
import { notificationsRouterRoute } from '@townsquare/notifications-view/__router-route';
import { projectRoute } from '@townsquare/project-view/__router-route';
import {
  projectDirectoryRouterRoute,
  projectNoWorkspaceDirectoryRouterRoute,
} from '@townsquare/projects-directory-view/__router-route';
import {
  agentProfileRouterRoute,
  embeddableDirectoryRouterRoute,
  kudosProfileRouterRoute,
  profileRouterRoute,
  teamProfileRouterRoute,
} from '@townsquare/ptc-directory-view/__router-route';
import { readingModeRouterRoute } from '@townsquare/reading-mode-view/__router-route';
import { applyResourcesToRoute, expandGlobalRoute } from '@townsquare/router/create-route';
import { HomepageRouteType, HomepageRoutes } from '@townsquare/router/route-types';
import { rovoEntitlementResource } from '@townsquare/rovo/entitlement';
import { rovoRouterRoute } from '@townsquare/rovo-view/__router-route';
import { searchConfigurationResource } from '@townsquare/search-view';
import { searchRouterRoute } from '@townsquare/search-view/__router-route';
import { noWorkspacePersonalSettingsRouterRoute, settingsRouterRoute } from '@townsquare/settings-view/__router-route';
import { staffDirectoryRouterRoute } from '@townsquare/staff-directory-view/__router-route';
import { tagsDirectoryRouterRoute } from '@townsquare/tags-directory-view/__router-route';
import {
  teamHealthMonitorEmbedRouterRoute,
  teamHealthMonitorRouterRoute,
  teamHealthRouterRoute,
} from '@townsquare/team-health-view/__router-route';
import { topicRouterRoute } from '@townsquare/topic-view/__router-route';
import { followGoalActionRoute } from '@townsquare/url-actions/follow-goal/route';
import { followProjectActionRoute } from '@townsquare/url-actions/follow-project/route';
import { lookupRedirectActionRoute } from '@townsquare/url-actions/lookup-redirect/route';
import { reactionUpdateActionRoute } from '@townsquare/url-actions/reaction-update/route';
import { searchRedirectActionRoute } from '@townsquare/url-actions/search-redirect/route';

// When adding a new route, please also add it to the route-sanity-check E2E test
export const routeDefinitions: HomepageRoutes = [
  // Actions
  followProjectActionRoute,
  followGoalActionRoute,
  lookupRedirectActionRoute,
  reactionUpdateActionRoute,
  searchRedirectActionRoute,

  // Routes
  // staffDirectoryRouterRoute needs to come before profileRouterRoute
  staffDirectoryRouterRoute,
  // profileRouterRoute needs to come before teamProfileRouterRoute and kuodsProfileRouterRoute
  profileRouterRoute,
  kudosProfileRouterRoute,
  teamProfileRouterRoute,
  agentProfileRouterRoute,
  createAgentRouterRoute,
  editAgentRouterRoute,
  chatRouterRoute,
  goalDirectoryRouterRoute,
  goalUpdateViewRouterRoute,
  goalViewRoute,
  notificationsRouterRoute,
  projectDirectoryRouterRoute,
  projectRoute,
  projectUpdateViewRouterRoute,
  readingModeRouterRoute,
  savedViewsUpdatesRouterRoute,
  searchRouterRoute,
  rovoRouterRoute,
  agentStudioRouterRoute,
  settingsRouterRoute,
  tagFeedViewRouterRoute,
  recentsViewRouterRoute,
  teamUpdatesViewRouterRoute,
  topicRouterRoute,
  tagsDirectoryRouterRoute,
  dashboardRouterRoute,
  dashboardDirectoryRouterRoute,
  teamHealthRouterRoute,
  teamHealthMonitorRouterRoute,
  appsRoute,

  // Embed routes
  createKudosEmbedRoute,
  createKudosEmbedRouteOld,
  goalEmbedRoute,
  goalsDirectoryEmbedRoute,
  labelEmbedRoute,
  projectEmbedRoute,
  projectsDirectoryEmbedRoute,
  teamHealthMonitorEmbedRouterRoute,

  // Fallback
  homeTabRoute,
  popupAuthRoute,
  notFoundRouterRoute,
];

export const noWorkspaceRouteDefinitions = [
  goalNoWorkspaceDirectoryRouterRoute,
  projectNoWorkspaceDirectoryRouterRoute,
  notificationsRouterRoute,
  emptyOrNonProvisionedStatusUpdateStateRoute,
  embeddableDirectoryRouterRoute,
  profileRouterRoute,
  teamProfileRouterRoute,
  agentProfileRouterRoute,
  createAgentRouterRoute,
  editAgentRouterRoute,
  chatRouterRoute,
  searchRouterRoute,
  rovoRouterRoute,
  recentsViewRouterRoute,
  agentStudioRouterRoute,
  noWorkspacePersonalSettingsRouterRoute,
  appsRoute,

  // Fallback
  homeTabRoute,
  notFoundRouterRoute,
];

export const routes = routeDefinitions
  .flatMap<HomepageRouteType>(route => {
    if (route.routeType === 'global') {
      return expandGlobalRoute(route);
    }

    return route;
  })
  .map(route => {
    if (route.layout === 'home' && !route.embed) {
      route = applyResourcesToRoute(route, [homebarSideNavigationResource]);
    }

    if (route.routeType === 'global' || route.routeType === 'site') {
      route = applyResourcesToRoute(route, [
        rovoEntitlementResource,
        searchConfigurationResource,
        capabilityProviderResource,
        dashboardExperienceResource,
        sidebarContentResource,
      ]);
    }

    return route;
  });

export const noWorkspaceRoutes = noWorkspaceRouteDefinitions
  .flatMap<HomepageRouteType>(route => {
    if (route.routeType === 'global') {
      return expandGlobalRoute(route);
    }

    return route;
  })
  .map(route => {
    if (route.routeType === 'global' || route.routeType === 'site') {
      route = applyResourcesToRoute(route, [rovoEntitlementResource, searchConfigurationResource]);
    }

    return route;
  });
